import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, Card, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { SelectionContext } from "../../contexts/SelectionContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useUser } from "../../contexts/UserContext";
import { useUserInterface } from "../../contexts/UserInterfaceContext";
import { useSearch } from "../../hooks/useSearch";
import { MediaItemDetailed } from "../../types";
import { apiFetch, REACT_APP_API_ROOT } from "../../utilities/api";
import { getMediaThumbnailSync, getMediaThumbnail, getMediaFull } from "../../utilities/authMediaAdapters";
import { getThumbnailIcon } from "../../utilities/mediaUtils";
import { getRelatedMedia, getDataSourceType } from '../../utilities/dataSourceAdapters';
import ErrorBoundary from "../common/ErrorBoundary";
import SearchBar from "../common/SearchBar";
import ToggleButton from "../common/ToggleButton";
import MediaViewer from "./MediaViewer";

// Feature flags
const SHOW_SEARCH_BAR = false; // Toggle search functionality

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 6,
    slidesToSlide: 6,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 6,
    slidesToSlide: 6,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 4,
    slidesToSlide: 4,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 2,
    slidesToSlide: 2,
  },
};

const MAX_ITEMS = 50; // Limit the number of items shown

// Enhanced thumbnail URL generation using media adapter pattern
const generateThumbnailUrl = async (item: MediaItemDetailed, userProfile: any): Promise<string> => {
  console.log('[MediaDrawer] 🖼️ Generating thumbnail for item using adapter pattern:', {
    id: item.id,
    name: item.name,
    dataSourceType: getDataSourceType(item)
  });

  try {
    // Use the media adapter - handles all data sources uniformly
    const thumbnailResult = await getMediaThumbnail(item, userProfile);
            console.log('[MediaDrawer] ✅ Got thumbnail from adapter:', {
          authMethod: thumbnailResult.authMethod,
          hasUrl: !!thumbnailResult.url,
          requiresBlobConversion: thumbnailResult.requiresBlobConversion
        });
    return thumbnailResult.url;
  } catch (error) {
    console.error('[MediaDrawer] ❌ Adapter failed, falling back to sync method:', error);
    
    // Fallback to sync method
    const thumbnailUrl = getMediaThumbnailSync(item);
    if (thumbnailUrl) {
      console.log('[MediaDrawer] 📎 Using sync thumbnail URL:', thumbnailUrl);
      return thumbnailUrl;
    }
    
    // Final fallback to icon
    const fallbackIcon = getThumbnailIcon(item.url || item.remote_details?.url || '');
    console.log('[MediaDrawer] 🎭 Using fallback icon:', fallbackIcon);
    return fallbackIcon;
  }
};

const MediaDrawer: React.FC = () => {
  const [previewItem, setPreviewItem] = useState<MediaItemDetailed | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Get all data from context - pure reactive component
  const { selectedEntity, setSelectedEntity, selectedEndpoint } = useContext(SelectionContext);
  const config = useConfig();
  const { profile } = useUser();
  
  // Use UserInterface context for MediaDrawer visibility and layout coordination
  const { state, setMediaDrawerVisible, setMediaDrawerManuallyClosedByUser } = useUserInterface();
  
  // PERFORMANCE MONITORING: Track what causes thumbnail cache rebuilds
  const cacheRebuildCountRef = useRef(0);
  const lastEntityIdRef = useRef<number | string | null>(null);
  const lastMediaCountRef = useRef<number>(0);
  const entityIdRef = useRef<number | string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Determine if we should show the toggle button (always show when entity is selected)
  const shouldShowToggleButton = useMemo(() => {
    // Show toggle button whenever an entity is selected, regardless of DataDrawer mode
    return selectedEntity !== null;
  }, [selectedEntity]);

  // Get media items using adapter function instead of direct property access
  const displayMediaItems = useMemo(() => {
    if (!selectedEntity) return [];
    
    // Use adapter function for consistent media extraction across data sources
    const items = getRelatedMedia(selectedEntity);
    console.log('[MediaDrawer] Adapter retrieved media items:', {
      entityId: selectedEntity.id,
      entityName: selectedEntity.name,
      isComplete: selectedEntity.isComplete,
      mediaCount: items.length,
      hasRelatedMedia: !!selectedEntity.relatedMedia,
      relatedMediaCount: selectedEntity.relatedMedia?.length || 0
    });
    
    if (items.length > 0) {
      console.log('[MediaDrawer] First media item structure:', items[0]);
      console.log('[MediaDrawer] First item has url:', !!items[0].url);
      console.log('[MediaDrawer] First item has remote_details:', !!items[0].remote_details);
    } else if (selectedEntity.isComplete === false) {
      console.log('[MediaDrawer] No media found, but entity is still loading...');
    } else {
      console.log('[MediaDrawer] No media found for complete entity');
    }
    
    return items;
  }, [selectedEntity]);

  // PERFORMANCE: Track stable media signature to prevent unnecessary cache rebuilds
  const mediaSignatureRef = useRef<string>('');
  const lastCacheRef = useRef<Map<string, Promise<string>>>(new Map());
  
  // OPTIMIZED: Create a truly stable thumbnail cache using ref-based signature comparison
  // Now handles async thumbnail generation for Fulcrum photos
  const thumbnailCache = useMemo(() => {
    const currentEntityId = selectedEntity?.id || null;
    const currentMediaCount = displayMediaItems.length;
    
    // Create current signature
    const currentSignature = `${currentEntityId}-${currentMediaCount}-${displayMediaItems.map(item => item.id).sort().join(',')}`;
    
    // Only rebuild if signature actually changed
    if (mediaSignatureRef.current === currentSignature && lastCacheRef.current.size > 0) {
      console.log(`🔍 [MediaDrawer] PERF: Cache signature unchanged, reusing existing cache (${lastCacheRef.current.size} entries)`);
      return lastCacheRef.current;
    }
    
    // Signature changed - rebuild cache
    cacheRebuildCountRef.current++;
    mediaSignatureRef.current = currentSignature;
    
    console.log(`🔍 [MediaDrawer] PERF: Thumbnail cache rebuild #${cacheRebuildCountRef.current}:`, {
      rebuildCount: cacheRebuildCountRef.current,
      entityId: currentEntityId,
      mediaCount: currentMediaCount,
      entityChanged: lastEntityIdRef.current !== currentEntityId,
      mediaCountChanged: lastMediaCountRef.current !== currentMediaCount,
      signatureChanged: true,
      currentSignature: currentSignature,
      prevEntityId: lastEntityIdRef.current,
      prevMediaCount: lastMediaCountRef.current
    });
    
    lastEntityIdRef.current = currentEntityId;
    lastMediaCountRef.current = currentMediaCount;
    
    console.log('[MediaDrawer] Building async thumbnail cache for', displayMediaItems.length, 'items');
    const cache = new Map<string, Promise<string>>();
    
    displayMediaItems.forEach(item => {
      const itemKey = item.id.toString();
      if (!cache.has(itemKey)) {
        // Store the promise in cache for async thumbnail generation
        const thumbnailPromise = generateThumbnailUrl(item, profile);
        cache.set(itemKey, thumbnailPromise);
      }
    });
    
    console.log('[MediaDrawer] Async thumbnail cache built with', cache.size, 'entries');
    lastCacheRef.current = cache;
    return cache;
  }, [selectedEntity?.id, displayMediaItems.length, displayMediaItems.map(item => item.id).join(',')]); // Intentional - let useMemo run, but we control rebuilding internally

  // State for tracking thumbnail loading status
  const [thumbnailStates, setThumbnailStates] = useState<Map<string, { url: string; loading: boolean; error: boolean }>>(new Map());

  // Effect to resolve thumbnail promises and update states
  useEffect(() => {
    const resolvePromises = async () => {
      const newStates = new Map<string, { url: string; loading: boolean; error: boolean }>();
      
      // Initialize loading states
      displayMediaItems.forEach(item => {
        const itemKey = item.id.toString();
        newStates.set(itemKey, { url: '', loading: true, error: false });
      });
      setThumbnailStates(newStates);
      
      // Resolve all thumbnail promises
      const promises = displayMediaItems.map(async (item) => {
        const itemKey = item.id.toString();
        const thumbnailPromise = thumbnailCache.get(itemKey);
        
        if (thumbnailPromise) {
          try {
            const url = await thumbnailPromise;
            setThumbnailStates(prev => {
              const updated = new Map(prev);
              updated.set(itemKey, { url, loading: false, error: false });
              return updated;
            });
          } catch (error) {
            console.error(`[MediaDrawer] Failed to load thumbnail for ${itemKey}:`, error);
            const fallbackUrl = getThumbnailIcon('');
            setThumbnailStates(prev => {
              const updated = new Map(prev);
              updated.set(itemKey, { url: fallbackUrl, loading: false, error: true });
              return updated;
            });
          }
        }
      });
      
      await Promise.all(promises);
    };
    
    if (displayMediaItems.length > 0) {
      resolvePromises();
    }
  }, [thumbnailCache, displayMediaItems]);

  // OPTIMIZED: Create a stable thumbnail URL getter that uses the resolved states
  const getThumbnailUrl = useCallback((item: MediaItemDetailed): { url: string; loading: boolean; error: boolean } => {
    const itemKey = item.id.toString();
    const state = thumbnailStates.get(itemKey);
    
    if (state) {
      return state;
    }
    
    // Fallback if somehow not found
    console.warn('[MediaDrawer] Item not found in thumbnail states, using fallback:', item.id);
    return {
      url: getThumbnailIcon(''),
      loading: false,
      error: true
    };
  }, [thumbnailStates]);

  const { searchTerm, setSearchTerm, filteredItems } = useSearch(displayMediaItems);

  // Take only the first MAX_ITEMS items after filtering
  const limitedItems = useMemo(
    () => filteredItems.slice(0, MAX_ITEMS),
    [filteredItems]
  );

  // Show a message if there are more items than we're displaying
  const hasMoreItems = filteredItems.length > MAX_ITEMS;

  const toggleDrawer = useCallback(() => {
    const newVisibility = !state.isMediaDrawerVisible;
    
    // Simple context-based toggle
    if (!newVisibility) {
      // User is closing the drawer
      setMediaDrawerManuallyClosedByUser(true);
    }
    setMediaDrawerVisible(newVisibility);
  }, [setMediaDrawerVisible, setMediaDrawerManuallyClosedByUser, state.isMediaDrawerVisible, state.dataDrawerMode]);

  // Constants for consistent styling
  const DRAWER_BOTTOM_MARGIN = 20;
  const Z_INDEX_TOGGLE_FULLSCREEN = 2000;
  const Z_INDEX_TOGGLE_COMPACT = 1400;
  const Z_INDEX_DRAWER_FULLSCREEN = 1200;
  const Z_INDEX_DRAWER_COMPACT = 1300;


  // Smart effect to manage MediaDrawer visibility - immediate action for complete entities, debounced for others
  useEffect(() => {

    if (!selectedEntity) {
      // Always hide when no entity
      if (state.isMediaDrawerVisible) {
        setMediaDrawerVisible(false);
      }
      return;
    }

    const hasMedia = selectedEntity.relatedMedia && selectedEntity.relatedMedia.length > 0;
    
    // Skip ALL auto-show logic if user recently manually closed (prevent render loop)
    if (state.mediaDrawerManuallyClosedByUser) {
      return;
    }
    
    // IMMEDIATE ACTION: If entity is complete and has media, open immediately (don't debounce)
    if (selectedEntity.isComplete === true && hasMedia && !state.isMediaDrawerVisible) {
      setMediaDrawerVisible(true);
      return; // Don't set timeout for this case
    }

    // Clear any existing timeout to prevent stale callbacks
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // DEBOUNCED ACTION: For all other cases (incomplete entities, hiding, etc.)
    const timeoutId = setTimeout(() => {

      // Skip if entity is still loading
      if (selectedEntity.isComplete === false) {
        return;
      }
      
      // Skip timeout callback if user has manually closed drawer
      if (state.mediaDrawerManuallyClosedByUser) {
        return;
      }
      
      const currentHasMedia = selectedEntity.relatedMedia && selectedEntity.relatedMedia.length > 0;
      
      // MediaDrawer now works in both compact and fullscreen modes
      
      // Auto-hide when entity has no media
      if (!currentHasMedia && state.isMediaDrawerVisible) {
        setMediaDrawerVisible(false);
      } 
      // Respect manual close - hide drawer if user manually closed it
      else if (state.mediaDrawerManuallyClosedByUser && state.isMediaDrawerVisible) {
        setMediaDrawerVisible(false);
      }
    }, 100); // Small delay to let state settle

    // Store the timeout reference
    timeoutRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
      if (timeoutRef.current === timeoutId) {
        timeoutRef.current = null;
      }
    };
  }, [
    selectedEntity?.id, 
    selectedEntity?.relatedMedia?.length, 
    selectedEntity?.isComplete, // Include isComplete to react to data loading completion
    state.dataDrawerMode, 
    state.isMediaDrawerVisible,
    state.mediaDrawerManuallyClosedByUser,
    setMediaDrawerVisible
  ]);

  // Reset manual flags when entity changes to allow auto-show for new entities
  useEffect(() => {
    const currentEntityId = selectedEntity?.id;
    const prevEntityId = entityIdRef.current;
    
    if (currentEntityId && currentEntityId !== prevEntityId) {
      
      // Reset manual flag when entity changes
      setMediaDrawerManuallyClosedByUser(false);
      entityIdRef.current = currentEntityId;
    }
  }, [selectedEntity?.id, selectedEntity?.name, setMediaDrawerManuallyClosedByUser]);

  // MediaDrawer now just consumes data from SelectionContext - no need to fetch data here
  // SelectionContext automatically handles complete data fetching when entities are selected

  const CustomLeftArrow = ({ onClick }: { onClick?: () => void }) => (
    <ArrowCircleLeftIcon
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        position: "absolute",
        left: 0,
        fontSize: "3rem",
        cursor: "pointer",
      }}
      onClick={onClick}
    />
  );

  const CustomRightArrow = ({ onClick }: { onClick?: () => void }) => (
    <ArrowCircleRightIcon
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        position: "absolute",
        right: 0,
        fontSize: "3rem",
        cursor: "pointer",
      }}
      onClick={onClick}
    />
  );

  const handleItemClick = useCallback(async (item: MediaItemDetailed) => {
    console.log('[MediaDrawer] Clicked media item:', {
      id: item.id,
      name: item.name,
      url: item.url,
      thumbnail: item.thumbnail,
      media_type: item.media_type,
      hasRemoteDetails: !!item.remote_details,
      remoteDetailsUrl: item.remote_details?.url,
      originalFile: item.original_file,
      dataSourceType: getDataSourceType(item),
      hasFulcrumPhoto: !!item.fulcrum_photo
    });
    
    // Close any existing preview first to ensure clean state
    setIsPreviewOpen(false);
    setPreviewItem(null);
    
    // Use setTimeout to ensure the previous dialog is fully closed before opening new one
    setTimeout(async () => {
      console.log('[MediaDrawer] 🔄 Using media adapters to prepare item for preview...');
      
      try {
        // Use adapters to get the best URLs - adapters handle all data source logic internally
        const [thumbnailResult, fullImageResult] = await Promise.all([
          getMediaThumbnail(item, profile),
          getMediaFull(item, profile)
        ]);
        
        console.log('[MediaDrawer] 📸 Media adapter results:', {
          thumbnail: { authMethod: thumbnailResult.authMethod, hasUrl: !!thumbnailResult.url, requiresBlobConversion: thumbnailResult.requiresBlobConversion },
          fullImage: { authMethod: fullImageResult.authMethod, hasUrl: !!fullImageResult.url, requiresBlobConversion: fullImageResult.requiresBlobConversion }
        });
        
        // Combine item with adapter-provided URLs
        const previewItem = {
          ...item,  // Use original item as base
          url: fullImageResult.url || item.url,           // Prefer adapter URL, fallback to original
          thumbnail: thumbnailResult.url || item.thumbnail // Prefer adapter thumbnail, fallback to original
        };
        
        console.log('[MediaDrawer] ✅ Successfully prepared item for preview using adapter pattern');
        
        setPreviewItem(previewItem);
        setIsPreviewOpen(true);
        
      } catch (error) {
        console.error('[MediaDrawer] ❌ Failed to prepare item for preview:', error);
        
        // Final fallback: use original item as-is
        console.log('[MediaDrawer] 🔄 Using original item as final fallback');
        setPreviewItem(item);
        setIsPreviewOpen(true);
      }
    }, 50);
  }, []);

  // Helper function to check if a media item can be selected as a standalone entity
  const canSelectAsEntity = useCallback((item: MediaItemDetailed): boolean => {
    const dataSourceType = getDataSourceType(item);
    
    // For now, only Django media items can be standalone entities
    // Fulcrum photos are just attachments to records
    return dataSourceType === 'django';
  }, []);

  // New function to handle selecting a media item as the selected entity
  const handleSelectAsEntity = useCallback((item: MediaItemDetailed, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the preview from opening
    
    // Note: This function should only be called for items that canSelectAsEntity() returns true
    // The button is conditionally rendered, so we shouldn't reach here for non-standalone items
    const dataSourceType = getDataSourceType(item);
    
    // Convert MediaItemDetailed to Entity interface
    // CRITICAL: Set entityType to 'media' so SelectionContext finds the correct media endpoint
    const entityFromMedia = {
      id: item.id,
      name: item.name,
      entityType: 'media', // Always use 'media' so SelectionContext finds the media endpoint
      coordinates: item.coordinates ? [item.coordinates[0], item.coordinates[1]] as [number, number] : undefined,
      geometry: item.geometry || undefined,
      properties: {
        ...item,
        fromMediaDrawer: true, // Mark source for debugging
        parentEntityId: selectedEntity?.id, // Track the parent entity
        parentEntityType: selectedEntity?.entityType, // Track the parent entity type
        parentEntityName: selectedEntity?.name // Track the parent entity name
      },
      relatedMedia: [item], // Include itself as related media
      hasMedia: true,
      // Django media items can be fetched independently, others are already complete
      isComplete: dataSourceType !== 'django', // Django media can be fetched, others are complete
      relationships: dataSourceType === 'django' ? undefined : {
        related_entities: {},
        geo_entities: {},
        media: [] // Non-Django media items don't have relationships
      }
    };
    
    console.log('🔴 [MediaDrawer] Selecting media item as entity:', {
      id: item.id,
      name: item.name,
      entityType: 'media', // This should trigger SelectionContext to find the media endpoint
      parentEntityId: selectedEntity?.id,
      parentEntityType: selectedEntity?.entityType,
      previousEndpoint: selectedEndpoint?.name
    });
    
    // Find the Media endpoint from config and pass it explicitly
    const mediaEndpoint = config.apiEndpoints.find(ep => ep.entityType === 'media');
    
    console.log('🔧 [MediaDrawer] Setting media entity with explicit endpoint:', {
      entityId: entityFromMedia.id,
      entityType: entityFromMedia.entityType,
      mediaEndpointFound: !!mediaEndpoint,
      mediaEndpointName: mediaEndpoint?.name
    });
    
    // Use SelectionContext to set this as the selected entity with the correct endpoint
    setSelectedEntity(entityFromMedia, mediaEndpoint);
  }, [setSelectedEntity, selectedEntity, selectedEndpoint]);

  // Show toggle button when entity is selected
  if (!shouldShowToggleButton) {
    return null;
  }

  return (
    <>
      {/* Toggle Button - Always visible when entity selected, purple indicator when media available */}
      <Box
        sx={{
          position: state.dataDrawerMode === 'fullscreen' ? "fixed" : "absolute",
          bottom: state.isMediaDrawerVisible ? 280 : 60, // More space in fullscreen mode
          left: "50%", // Center in available space
          transform: "translateX(-50%)",
          zIndex: state.dataDrawerMode === 'fullscreen' ? Z_INDEX_TOGGLE_FULLSCREEN : Z_INDEX_TOGGLE_COMPACT,
          transition: "bottom 0.3s ease-in-out",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          gap: 1,
        }}
      >
        {state.isMediaDrawerVisible && hasMoreItems && (
          <Typography
            variant="caption"
            sx={{
              color: "white",
              textAlign: "left",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              padding: "4px 8px",
              borderRadius: "4px",
              position: "absolute",
              left: "24px",
              bottom: "100%",
              marginBottom: "8px",
            }}
          >
            Showing first {MAX_ITEMS} of {displayMediaItems.length} items
          </Typography>
        )}
        <ToggleButton
          isOpen={state.isMediaDrawerVisible}
          onClick={toggleDrawer}
          direction="vertical"
          showIndicator={displayMediaItems.length > 0}
          reverseDirection={true}
        />
        {/* Media count indicator */}
        {displayMediaItems.length > 0 && !state.isMediaDrawerVisible && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              backgroundColor: "primary.main",
              color: "white",
              borderRadius: "50%",
              minWidth: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.7rem",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {displayMediaItems.length}
          </Typography>
        )}
      </Box>

      {/* Drawer Content - Only shown when explicitly opened */}
      {state.isMediaDrawerVisible && (
        <ErrorBoundary
          resetKeys={[selectedEntity?.id, displayMediaItems.length].filter(key => key !== undefined)}
          onError={(error, errorInfo) => {
            console.error('MediaDrawer Error:', error, errorInfo);
          }}
        >
          <Box
            sx={{
              position: state.dataDrawerMode === 'fullscreen' ? "fixed" : "absolute",
              bottom: DRAWER_BOTTOM_MARGIN,
              left: state.dataDrawerMode === 'fullscreen' ? 20 : 5, // More margin for fullscreen
              right: state.dataDrawerMode === 'fullscreen' ? 20 : 5, // More margin for fullscreen
              width: state.dataDrawerMode === 'fullscreen' ? "calc(100vw - 40px)" : "auto", // Full width minus margins for fullscreen
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              height: "auto", // Let content determine height
              display: "flex",
              flexDirection: "column",
              zIndex: state.dataDrawerMode === 'fullscreen' ? Z_INDEX_DRAWER_FULLSCREEN : Z_INDEX_DRAWER_COMPACT,
              pointerEvents: "auto", // Ensure the drawer itself is clickable
              borderRadius: "8px", // Rounded corners all around now
              paddingBottom: 0, // Remove any bottom padding
              '& .react-multi-carousel-list': {
                paddingBottom: 0,
                marginBottom: 0,
              }
            }}
      >
        {displayMediaItems.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
                height: '100%',
                flexDirection: 'column',
                gap: 2
              }}>
                {selectedEntity && !selectedEntity.isComplete ? (
                  <>
                    <CircularProgress size={40} sx={{ color: 'white' }} />
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      Loading media for {selectedEntity.name}...
                    </Typography>
                  </>
                ) : (
            <Typography variant="body1" sx={{ color: 'white' }}>
              No media items found for the selected entity
            </Typography>
                )}
          </Box>
        ) : (
                      <>
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              '& .react-multi-carousel-list': {
                margin: 0,
                padding: 0,
              },
              '& .react-multi-carousel-track': {
                margin: 0,
                padding: 0,
              },
              '& .react-multi-carousel-item': {
                margin: 0,
                padding: 0,
              },
              marginBottom: 0, // Remove margin from inside the background container
            }}>
              <Carousel
                responsive={responsive}
                customLeftArrow={<CustomLeftArrow />}
                customRightArrow={<CustomRightArrow />}
                itemClass="carousel-item"
                centerMode
                infinite={false}
                removeArrowOnDeviceType={["mobile"]}
                ssr={false}
                partialVisible={false}
              >
              {limitedItems.map((item) => (
                <Box key={item.id} sx={{ margin: 0.25, position: "relative" }}>
                  <Card
                    sx={{
                      height: {
                        xs: 165, // Mobile: increased height by 150%
                        sm: 180, // Small tablets: increased height by 150%
                        md: 195, // Medium screens: increased height by 150%
                        lg: 210, // Large screens: increased height by 150%
                      },
                      cursor: "pointer",
                      backgroundColor: "rgba(26, 26, 26, 0.9)", // Modern dark background like DataItemCard
                      backdropFilter: "blur(8px)", // Modern blur effect
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)", // Modern shadow
                      border: "2px solid black", // Black border around the entire card
                      borderRadius: 2, // Modern rounded corners
                      transition: "all 0.3s ease-out", // Smooth transitions
                      "&:hover": {
                        border: "2px solid white",
                        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)", // Enhanced shadow on hover
                        transform: "translateY(-2px)", // Subtle lift on hover
                      },
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden", // Ensure clean edges
                    }}
                    onClick={() => handleItemClick(item)}
                  >
                    {/* Main image area */}
                    <Box
                      sx={(() => {
                        // Get thumbnail state once for all calculations
                        const thumbnailState = getThumbnailUrl(item);
                        const isIcon = thumbnailState.url.includes('/icons/');
                        
                        return {
                          flex: 1,
                          display: "flex",
                          alignItems: isIcon ? "center" : "flex-start", // Center icons, top-align images
                          justifyContent: "center",
                          position: "relative",
                          overflow: "hidden",
                          padding: isIcon ? 1 : 0, // Only pad icons, not images
                        };
                      })()}
                    >
                      {(() => {
                        // Get cached thumbnail URL to avoid multiple calls
                        const thumbnailState = getThumbnailUrl(item);
                        const isIcon = thumbnailState.url.includes('/icons/');
                        
                        // Show loading state for Fulcrum photos
                        if (thumbnailState.loading) {
                          return (
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255, 255, 255, 0.1)"
                              }}
                            >
                              <CircularProgress size={30} sx={{ color: 'white' }} />
                            </Box>
                          );
                        }
                        
                        return (
                          <Box
                            component="img"
                            src={thumbnailState.url}
                            alt={item.name}
                            onError={(e) => {
                              // Handle 403 errors from CDN by falling back to default icon
                              const target = e.target as HTMLImageElement;
                              const fallbackIcon = getThumbnailIcon(item.url || item.name);
                              if (target.src !== fallbackIcon) {
                                console.log('[MediaDrawer] 🚨 Image load failed, using fallback icon:', {
                                  itemId: item.id,
                                  originalSrc: thumbnailState.url,
                                  fallbackIcon
                                });
                                target.src = fallbackIcon;
                                // Force re-render to apply correct styling for the icon
                                target.style.width = "60px";
                                target.style.height = "60px";
                                target.style.objectFit = "contain";
                              }
                            }}
                            sx={{
                              width: isIcon ? "60px" : "100%",
                              height: isIcon ? "60px" : "100%",
                              objectFit: isIcon ? "contain" : "cover",
                              borderRadius: 0, // Remove border radius for cleaner look
                              opacity: thumbnailState.error ? 0.7 : 1, // Dim failed images slightly
                            }}
                          />
                        );
                      })()}
                      
                      {/* Selection button - only show for media items that can be standalone entities */}
                      {canSelectAsEntity(item) && (
                        <Tooltip title="Select as main entity" arrow>
                          <IconButton
                            onClick={(e) => handleSelectAsEntity(item, e)}
                            sx={{
                              position: "absolute",
                              top: 4, // Closer to the border
                              right: 4, // Closer to the border
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              color: "white",
                              width: 28, // Slightly smaller
                              height: 28, // Slightly smaller
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.9)",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                            size="small"
                          >
                            <LocationOnIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    
                    {/* Title and description area - compact with centered text */}
                    <Box
                      sx={{
                        padding: 1, // Reduced padding for less height
                        backgroundColor: "rgba(0, 0, 0, 0.8)", // Slightly darker background for text area
                        borderTop: "1px solid rgba(255, 255, 255, 0.1)", // Subtle separator
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center", // Center content vertically
                        alignItems: "center", // Center content horizontally
                        gap: 0.25, // Smaller gap between title and description
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          textAlign: "center", // Center text like DataItemCard
                          color: "white",
                          fontSize: "0.7rem", // Match DataItemCard font size
                          fontWeight: 500, // Match DataItemCard font weight
                          lineHeight: 1.2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 1, // Single line for title
                          WebkitBoxOrient: "vertical",
                          mb: 0,
                          width: '100%'
                        }}
                      >
                        {item.name}
                      </Typography>
                      
                      {item.description && (
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: "center", // Center description too
                            color: "rgba(255, 255, 255, 0.7)", // Slightly muted color for description
                            fontSize: "0.65rem", // Smaller font for description
                            lineHeight: 1.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 1, // Single line for description
                            WebkitBoxOrient: "vertical",
                            mb: 0,
                            width: '100%'
                          }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                  </Card>
                </Box>
              ))}
            </Carousel>
            </Box>

            {SHOW_SEARCH_BAR && (
              <Box
                sx={{
                  mt: "auto",
                  px: 2,
                  pb: 1, // Reduced bottom padding to minimize gap
                }}
              >
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by name, description or type..."
                />
              </Box>
            )}
          </>
        )}
          </Box>
        </ErrorBoundary>
      )}

      {/* OPTIMIZED: Only render MediaViewer when needed to prevent unnecessary instantiation */}
      {isPreviewOpen && previewItem && (
        <MediaViewer
          item={previewItem}
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          mode="dialog"
        />
      )}
    </>
  );
};

export default MediaDrawer;
